// Tap-to-explain navigation on the details screen. The quick cards at the top
// compress each fact into an icon + value with no room for a label; tapping one
// scrolls to the full section below that explains it and pulses that section
// (grow-shrink + accent outline flash — see .pulse-highlight in styles.css).
// When no target section exists yet — /mnt still loading, errored, or the
// vehicle simply lacks that data — the tapped element pulses in place instead,
// so every tap still visibly responds.

const PULSE_CLASS = 'pulse-highlight';
const DELAY_CLASS = 'pulse-delayed';
// Removal must outlive the CSS animation (1.4 s) plus the scroll delay (0.3 s).
const PULSE_TOTAL_MS = 1900;

const timers = new WeakMap<Element, number>();

function pulse(el: HTMLElement, delayed: boolean): void {
  const existing = timers.get(el);
  if (existing !== undefined) window.clearTimeout(existing);
  el.classList.remove(PULSE_CLASS, DELAY_CLASS);
  // Force a reflow so re-adding the class restarts the animation on repeat taps.
  void el.offsetWidth;
  el.classList.add(PULSE_CLASS);
  if (delayed) el.classList.add(DELAY_CLASS);
  timers.set(
    el,
    window.setTimeout(() => {
      el.classList.remove(PULSE_CLASS, DELAY_CLASS);
      timers.delete(el);
    }, PULSE_TOTAL_MS),
  );
}

function scrollIntoViewWithFallback(target: HTMLElement, smooth: boolean): void {
  if (!smooth) {
    target.scrollIntoView({ behavior: 'auto', block: 'start' });
    return;
  }
  const before = target.getBoundingClientRect().top;
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // Some embedded WebViews silently ignore smooth scrolling (without the
  // reduced-motion media query reporting it): if nothing has moved shortly
  // after, and the section isn't already at the top, jump instantly.
  window.setTimeout(() => {
    const after = target.getBoundingClientRect().top;
    const moved = Math.abs(after - before) >= 1;
    const alreadyThere = before >= 0 && before <= 60;
    if (!moved && !alreadyThere) target.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, 250);
}

/**
 * Scroll to the first section id that exists in the DOM and pulse it. Ids are
 * tried in order, so callers can name a preferred target plus fallbacks (e.g.
 * the status pills, else the operations history). With no reachable target the
 * event's currentTarget pulses in place.
 */
export function scrollToSection(ids: readonly string[], event?: Event): void {
  const target = ids
    .map((id) => document.getElementById(id))
    .find((el): el is HTMLElement => el !== null);
  if (!target) {
    const source = event?.currentTarget;
    if (source instanceof HTMLElement) pulse(source, false);
    return;
  }
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  scrollIntoViewWithFallback(target, !reduceMotion);
  // Delay the pulse a beat so it plays as the smooth scroll lands on it.
  pulse(target, !reduceMotion);
}
